import axios from 'axios';
import {
    notifyAuthExpired,
    isAuthExpiredError,
    isAuthExpiredForceFailActive,
    isAuthExpiredResponse,
} from '../utils/auth-error-utils';

class RestAPIUtils {
    static async makeGetRequestNoAuth(config, apiPath, params = {}) {
        const { apiUrl, apiKey } = config;

        const headers = {
            'x-api-key': apiKey,
        };

        try {
            // console.warn(`makeGetRequestNoAuth API at ${apiUrl}${apiPath}`);
            // console.warn(`Headers: ${JSON.stringify(headers)}`);

            const response = await axios.get(`${apiUrl}${apiPath}`, {
                headers: headers,
                params: params,
            });

            if (response.status === 200) {
                return response.data;
            } else {
                throw new Error(`Received status code ${response.status}`);
            }
        } catch (error) {
            console.error(`An error occurred: ${error}`);
            throw error;
        }
    }

    static async makeGetRequest(bearerToken, config, apiPath, params = {}) {
        const { apiUrl, apiKey } = config;

        if (isAuthExpiredForceFailActive()) {
            const err = new Error('Session expired (forced)');
            err.isAuthExpired = true;
            notifyAuthExpired({ source: 'dev-override', mode: 'fail' });
            throw err;
        }

        if (!bearerToken || bearerToken.trim() === '') {
            console.error('Bearer token is null or empty');
            throw new Error('Bearer token is null or empty');
        }

        const headers = {
            Authorization: `Bearer ${bearerToken}`,
            'x-api-key': apiKey,
        };

        try {
            const response = await axios.get(`${apiUrl}${apiPath}`, {
                headers,
                params,
                // Do not throw automatically on 4xx/5xx
                validateStatus: () => true,
            });
            if (response.status >= 200 && response.status < 300) {
                return response.data;
            }
            const msg = response.data?.message || response.statusText || `HTTP ${response.status}`;
            const err = new Error(msg);
            err.response = response;
            err.isAuthExpired = isAuthExpiredResponse(response, msg);
            if (err.isAuthExpired) {
                notifyAuthExpired({ source: 'api', status: response.status, message: msg });
            }
            throw err;
        } catch (error) {
            const msg = error?.response?.data?.message || error?.message || 'Network error';
            const err = new Error(msg);
            err.response = error?.response;
            err.config = error?.config;
            err.originalError = error;
            err.isAuthExpired = isAuthExpiredError(error);
            if (err.isAuthExpired) {
                notifyAuthExpired({ source: 'api', status: err.response?.status, message: msg });
            }
            console.error(`An error occurred: ${msg}`);
            throw err;
        }
    }

    static async makeWriteRequest(bearerToken, config, apiPath, method, data = {}) {
        const { apiUrl, apiKey } = config;

        if (isAuthExpiredForceFailActive()) {
            const err = new Error('Session expired (forced)');
            err.isAuthExpired = true;
            notifyAuthExpired({ source: 'dev-override', mode: 'fail' });
            throw err;
        }

        if (!bearerToken || bearerToken.trim() === '') {
            console.error('Bearer token is null or empty');
            throw new Error('Bearer token is null or empty');
        }

        if (!apiPath || apiPath.trim() === '') {
            console.error('API path is null or empty');
            throw new Error('API path is null or empty');
        }

        if (!method || method.trim() === '') {
            console.error('Method is null or empty');
            throw new Error('Method is null or empty');
        }

        const headers = {
            Authorization: `Bearer ${bearerToken}`,
            'x-api-key': apiKey,
            'Content-Type': 'application/json',
        };

        try {
            console.warn(`Calling API at ${apiUrl}${apiPath}`);
            console.warn(`Headers: ${JSON.stringify(headers)}`);

            const response = await axios({
                method,
                url: `${apiUrl}${apiPath}`,
                headers,
                data,
                // Do not throw automatically on 4xx/5xx
                validateStatus: () => true,
            });

            if (response.status >= 200 && response.status < 300) {
                return response.data; // may contain { warnings: [...] }
            }

            const msg = response.data?.message || response.statusText || `HTTP ${response.status}`;
            const err = new Error(msg);
            err.response = response;
            err.isAuthExpired = isAuthExpiredResponse(response, msg);
            if (err.isAuthExpired) {
                notifyAuthExpired({ source: 'api', status: response.status, message: msg });
            }
            throw err;
        } catch (error) {
            const msg = error?.response?.data?.message || error?.message || 'Network error';
            const err = new Error(msg);
            err.response = error?.response;
            err.config = error?.config;
            err.originalError = error;
            err.isAuthExpired = isAuthExpiredError(error);
            if (err.isAuthExpired) {
                notifyAuthExpired({ source: 'api', status: err.response?.status, message: msg });
            }
            console.error(`An error occurred: ${msg}`);
            throw err;
        }
    }

    static async makePartnerRequest(bearerToken = null, config, apiPath, method = 'GET', data = {}) {
        const { apiUrl } = config;

        const headers = {
            'Content-Type': 'application/json',
        };

        if (bearerToken) {
            headers['Authorization'] = `Bearer ${bearerToken}`;
        }

        try {
            console.warn(`makePartnerRequest API at ${apiUrl}${apiPath}`);
            console.warn(`Headers: ${JSON.stringify(headers)}`);

            const response = await axios({
                method: method,
                url: `${apiUrl}${apiPath}`,
                headers: headers,
                params: method === 'GET' ? data : undefined,
                data: method !== 'GET' ? data : undefined,
            });

            if (response.status === 200 || response.status === 201) {
                return response.data;
            } else {
                throw new Error(`Received status code ${response.status}`);
            }
        } catch (error) {
            console.error(`An error occurred: ${error}`);
            throw error;
        }
    }
}

export default RestAPIUtils;
